import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy } from 'lucide-react';
import {
  listApiKeys,
  createApiKey,
  revokeApiKey,
  ApiKey,
  ApiKeyRole,
  CreateApiKeyResponse,
} from '../api/apiKeys';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { PageHeader } from '../components/PageHeader';

const ROLES: ApiKeyRole[] = ['Viewer', 'Editor', 'Admin'];

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
}

export function AdminApiKeysPage() {
  const qc = useQueryClient();
  const { data: keys, isLoading, isError } = useQuery({
    queryKey: ['api-keys'],
    queryFn: listApiKeys,
  });

  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState<ApiKeyRole>('Viewer');
  const [newExpires, setNewExpires] = useState('');
  const [createdKey, setCreatedKey] = useState<CreateApiKeyResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: createApiKey,
    onSuccess: result => {
      setCreatedKey(result);
      setNewName('');
      setNewRole('Viewer');
      setNewExpires('');
      qc.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: revokeApiKey,
    onSuccess: () => {
      setConfirmRevoke(null);
      qc.invalidateQueries({ queryKey: ['api-keys'] });
    },
    onError: () => setConfirmRevoke(null),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    createMutation.mutate({
      name: newName.trim(),
      role: newRole,
      expiresAt: newExpires ? new Date(newExpires).toISOString() : null,
    });
  };

  const copyKey = async () => {
    if (!createdKey) return;
    await navigator.clipboard.writeText(createdKey.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isLoading) return <p className="text-muted-foreground">Loading…</p>;
  if (isError) return <p className="text-destructive">Failed to load API keys.</p>;

  return (
    <div className="space-y-6">
      <PageHeader
        title="API keys"
        description={
          <>
            Static credentials for third-party clients. Send as <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">X-Api-Key</code> header.
          </>
        }
      />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Create new key</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <Input
                type="text"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Name (e.g. third-party-svc)"
                required
              />
              <Select value={newRole} onValueChange={v => setNewRole(v as ApiKeyRole)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={newExpires}
                onChange={e => setNewExpires(e.target.value)}
              />
              <Button type="submit" disabled={createMutation.isPending || !newName.trim()}>
                {createMutation.isPending ? 'Creating…' : 'Create key'}
              </Button>
            </div>
            {createMutation.isError && (
              <p className="text-xs text-destructive">Failed to create key.</p>
            )}
          </form>
        </CardContent>
      </Card>

      {createdKey && (
        <div className="rounded-lg border border-warning/30 bg-warning/10 p-4 space-y-2">
          <h3 className="text-sm font-semibold text-warning">
            Copy this key now — it will not be shown again
          </h3>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-background border border-border px-3 py-2 rounded-md font-mono text-sm break-all">
              {createdKey.key}
            </code>
            <Button size="sm" onClick={copyKey}>
              <Copy className="h-4 w-4" />
              {copied ? 'Copied ✓' : 'Copy'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setCreatedKey(null)}>
              Dismiss
            </Button>
          </div>
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Prefix</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Created</TableHead>
            <TableHead>Expires</TableHead>
            <TableHead>Last used</TableHead>
            <TableHead>Status</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {keys?.map((k: ApiKey) => {
            const revoked = k.revokedAt !== null;
            return (
              <TableRow key={k.id} className={revoked ? 'opacity-50 hover:bg-transparent' : undefined}>
                <TableCell>{k.name}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{k.prefix}…</TableCell>
                <TableCell>{k.role}</TableCell>
                <TableCell className="text-muted-foreground text-xs">{formatDate(k.createdAt)}</TableCell>
                <TableCell className="text-muted-foreground text-xs">{formatDate(k.expiresAt)}</TableCell>
                <TableCell className="text-muted-foreground text-xs">{formatRelative(k.lastUsedAt)}</TableCell>
                <TableCell>
                  {revoked
                    ? <Badge variant="destructive">Revoked</Badge>
                    : <Badge variant="success">Active</Badge>}
                </TableCell>
                <TableCell>
                  {!revoked && (
                    confirmRevoke === k.id ? (
                      <span className="flex items-center gap-2 justify-end">
                        <Button
                          variant="ghost"
                          size="xs"
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => revokeMutation.mutate(k.id)}
                        >
                          Confirm
                        </Button>
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => setConfirmRevoke(null)}
                        >
                          Cancel
                        </Button>
                      </span>
                    ) : (
                      <div className="flex justify-end">
                        <Button
                          variant="ghost"
                          size="xs"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => setConfirmRevoke(k.id)}
                        >
                          Revoke
                        </Button>
                      </div>
                    )
                  )}
                </TableCell>
              </TableRow>
            );
          })}
          {keys?.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} className="p-6 text-center text-muted-foreground">
                No API keys yet
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
